import { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import {
  cyberGlassBackdrop,
  cyberGlassHairline,
  cyberGlassSurface,
  cyberGlassTextMuted,
  cyberGlassTextPrimary,
} from '../../styles/cyberGlass';
import { gradientPillCss } from '../../styles/gradientPill';

type AccountAccessShellProps = {
  eyebrow?: string;
  title: ReactNode;
  copy?: ReactNode;
  children: ReactNode;
};

const Page = styled.div`
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 1.5rem;
  background:
    radial-gradient(110% 120% at 0% 0%, rgba(224, 161, 94, 0.12) 0%, rgba(224, 161, 94, 0) 42%),
    radial-gradient(80% 100% at 100% 0%, rgba(159, 215, 255, 0.12) 0%, rgba(159, 215, 255, 0) 46%),
    linear-gradient(180deg, #07080c 0%, #10131a 100%);
`;

const Backdrop = styled.div`
  width: min(100%, 34rem);
  ${cyberGlassBackdrop};
  border-radius: 1.25rem;
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  padding: 1.5rem;
  border-radius: 1.25rem;
  color: ${cyberGlassTextPrimary};
  ${cyberGlassSurface};
  display: grid;
  gap: 1rem;
`;

const Header = styled.div`
  display: grid;
  gap: 0.55rem;
`;

const Eyebrow = styled.p`
  margin: 0;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #e0a15e;
  font-weight: 700;
`;

export const AccountAccessTitle = styled.h1`
  margin: 0;
  font-size: clamp(1.9rem, 4vw, 2.5rem);
  line-height: 1.04;
  color: #f5f7fb;
`;

export const AccountAccessCopy = styled.p`
  margin: 0;
  color: ${cyberGlassTextMuted};
  font-size: 0.98rem;
  line-height: 1.6;
`;

export const AccountAccessMeta = styled.div`
  padding: 0.9rem 1rem;
  border-radius: 0.95rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: grid;
  gap: 0.25rem;
`;

export const AccountAccessMetaLabel = styled.span`
  font-size: 0.74rem;
  color: ${cyberGlassTextMuted};
  text-transform: uppercase;
  letter-spacing: 0.16em;
`;

export const AccountAccessMetaValue = styled.span`
  font-size: 0.98rem;
  color: #f5f7fb;
  word-break: break-word;
`;

export const AccountAccessForm = styled.form`
  display: grid;
  gap: 0.9rem;
`;

export const AccountAccessFieldStack = styled.div`
  display: grid;
  gap: 0.35rem;
`;

export const AccountAccessLabel = styled.label`
  font-size: 0.82rem;
  color: ${cyberGlassTextMuted};
`;

export const accountAccessInputCss = css`
  background: rgba(10, 12, 18, 0.48);
  color: ${cyberGlassTextPrimary};
  border: 1px solid ${cyberGlassHairline};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);

  &::placeholder {
    color: ${cyberGlassTextMuted};
  }

  &:focus {
    outline: none;
    border-color: rgba(159, 215, 255, 0.74);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.04),
      0 0 0 3px rgba(159, 215, 255, 0.12);
  }
`;

export const AccountAccessInfoText = styled.p`
  margin: 0;
  color: #9fd7ff;
  font-size: 0.88rem;
`;

export const AccountAccessErrorText = styled.p`
  margin: 0;
  color: #ff8a8a;
  font-size: 0.88rem;
`;

export const AccountAccessActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

export const AccountAccessPrimaryButton = styled.button`
  ${gradientPillCss};
  text-transform: uppercase;
`;

export const AccountAccessSecondaryButton = styled.button`
  padding: 0.82rem 1.2rem;
  border-radius: 999px;
  border: 1px solid ${cyberGlassHairline};
  background: rgba(9, 14, 34, 0.44);
  color: ${cyberGlassTextPrimary};
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.64;
    cursor: not-allowed;
  }
`;

export default function AccountAccessShell({
  eyebrow = 'CyBard Account Access',
  title,
  copy,
  children,
}: AccountAccessShellProps) {
  return (
    <Page>
      <Backdrop>
        <Card>
          <Header>
            <Eyebrow>{eyebrow}</Eyebrow>
            <AccountAccessTitle>{title}</AccountAccessTitle>
            {copy ? <AccountAccessCopy>{copy}</AccountAccessCopy> : null}
          </Header>
          {children}
        </Card>
      </Backdrop>
    </Page>
  );
}
